// Relative, not the barrel: the early binding imports this module, and the barrel would pull React in ahead of its listener.
// Rotates, unlike ./session-id.ts: a parked tab kept one sessionId for 8.6 days. sessionStorage gives each tab its own visit; the idle window bounds a tab left open for days; neither alone suffices.
import { randomUUID } from "../random-uuid";
import { resolveSessionStorage, type StorageLike } from "./storage";

export const VISIT_ID_KEY_SUFFIX = "logging.visitId.v1";
export const VISIT_LAST_SEEN_KEY_SUFFIX = "logging.visitId.lastSeenAt.v1";

export const VISIT_IDLE_WINDOW_MS = 30 * 60 * 1000;

// Coalesces a burst of input into one storage write.
export const VISIT_TOUCH_GRANULARITY_MS = 60 * 1000;

// User input only, never event emission: emitting events counted as activity kept a parked tab's visit alive forever (automatic pressure/visibility events with nobody present).
export const VISIT_ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
] as const;

interface Visit {
  id: string;
  lastSeenAt: number;
  persistedAt: number;
}

export type ActivityTarget = Pick<
  Document,
  "addEventListener" | "removeEventListener" | "visibilityState"
>;

export interface VisitIdKeys {
  readonly idKey: string;
  readonly lastSeenKey: string;
}

export interface VisitIdSource extends VisitIdKeys {
  readonly getVisitId: (now?: number, storage?: StorageLike | null) => string;
  readonly touchVisit: (now?: number, storage?: StorageLike | null) => void;
  readonly trackVisitActivity: (
    target?: ActivityTarget,
    touch?: () => void,
  ) => () => void;
  readonly resetVisitIdState: () => void;
}

export function createVisitIdSource({
  idKey,
  lastSeenKey,
}: VisitIdKeys): VisitIdSource {
  // Closure state keeps getVisitId() (called per event) off Web Storage except to mint.
  let current: Visit | null = null;

  // Test-only: vitest shares one source per file.
  function resetVisitIdState(): void {
    current = null;
  }

  function load(storage: StorageLike | null): Visit | null {
    if (storage === null) return null;
    try {
      const id = storage.getItem(idKey);
      const lastSeen = storage.getItem(lastSeenKey);
      if (id === null || lastSeen === null) return null;
      const lastSeenAt = Number(lastSeen);
      if (!Number.isFinite(lastSeenAt)) return null;
      return { id, lastSeenAt, persistedAt: lastSeenAt };
    } catch {
      return null;
    }
  }

  function persist(storage: StorageLike | null, visit: Visit): Visit {
    if (storage === null) return visit;
    try {
      storage.setItem(idKey, visit.id);
      storage.setItem(lastSeenKey, String(visit.lastSeenAt));
      return { ...visit, persistedAt: visit.lastSeenAt };
    } catch {
      // Quota or private mode: the visit stays in memory for this page load.
      return visit;
    }
  }

  function resolve(now: number, storage: StorageLike | null): Visit {
    const known = current ?? load(storage);
    if (known && now - known.lastSeenAt <= VISIT_IDLE_WINDOW_MS) return known;
    return persist(storage, {
      id: randomUUID(),
      lastSeenAt: now,
      persistedAt: NaN,
    });
  }

  // A pure read apart from the mint: it must not count as activity or extend the idle window.
  function getVisitId(
    now: number = Date.now(),
    storage: StorageLike | null = resolveSessionStorage(),
  ): string {
    current = resolve(now, storage);
    return current.id;
  }

  function touchVisit(
    now: number = Date.now(),
    storage: StorageLike | null = resolveSessionStorage(),
  ): void {
    const visit = resolve(now, storage);
    const touched = { ...visit, lastSeenAt: now };
    current =
      now - visit.persistedAt >= VISIT_TOUCH_GRANULARITY_MS
        ? persist(storage, touched)
        : touched;
  }

  // Capture so a stopPropagation below cannot hide activity; passive so scrolling is never delayed. Becoming visible counts (someone returning to a parked tab) but going hidden does not.
  function trackVisitActivity(
    target: ActivityTarget = document,
    touch: () => void = () => touchVisit(),
  ): () => void {
    const onVisibility = () => {
      if (target.visibilityState === "visible") touch();
    };
    const options = { capture: true, passive: true } as const;
    for (const type of VISIT_ACTIVITY_EVENTS) {
      target.addEventListener(type, touch, options);
    }
    target.addEventListener("visibilitychange", onVisibility);
    return () => {
      for (const type of VISIT_ACTIVITY_EVENTS) {
        target.removeEventListener(type, touch, options);
      }
      target.removeEventListener("visibilitychange", onVisibility);
    };
  }

  return {
    idKey,
    lastSeenKey,
    getVisitId,
    touchVisit,
    trackVisitActivity,
    resetVisitIdState,
  };
}
